import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material'

import { CarbonSpec } from '../../types/carbon/models/CarbonSpec'
import useTableData from '../../hooks/useTableData'
import { PlatinumTableWrapper } from '../platinum-table-new/PlatinumTableWrapper'

interface SpecRunPreviewDialogProps {
  open: boolean
  customer: string
  job: string
  /** The spec to run. The dialog runs it fresh each time it opens. */
  spec: CarbonSpec | null
  onClose: () => void
}

/**
 * Inline Run Spec preview (#968 Obs 5): a popup table showing what the data
 * looks like with the current spec applied, without leaving the spec editor.
 *
 * Used by the dashboard's Edit Data flow, where the old behaviour — stash the
 * spec in sessionStorage and announce "running on Tables page..." — was
 * meaningless: inside a modal there is no Tables page to go to. The preview is
 * ephemeral: nothing is written to sessionStorage and nothing is saved; the
 * single Close Preview button returns to Edit Spec.
 */
export const SpecRunPreviewDialog: React.FC<SpecRunPreviewDialogProps> = ({
  open,
  customer,
  job,
  spec,
  onClose,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ sx: { width: '85vw', height: '80vh', maxWidth: 1400 } }}
    >
      <DialogTitle>Spec Preview</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {spec && (
          <SpecRunPreviewContent customer={customer} job={job} spec={spec} />
        )}
      </DialogContent>
      <DialogActions sx={{ borderTop: 1, borderColor: 'divider', px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained" color="info">
          Close Preview
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/**
 * Split out so useTableData only runs while a spec is actually being
 * previewed — the dialog shell renders (closed) with spec null.
 */
const SpecRunPreviewContent: React.FC<{
  customer: string
  job: string
  spec: CarbonSpec
}> = ({ customer, job, spec }) => {
  const tableData = useTableData({
    customer,
    job,
    table: '',
    filter: '',
    useCache: false,
    spec: spec as unknown as Record<string, unknown>,
  })

  if (tableData.loading) {
    return (
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (tableData.isError) {
    return (
      <Alert severity="error" sx={{ mt: 1 }}>
        Failed to run spec: {tableData.error instanceof Error ? tableData.error.message : 'unknown error'}
      </Alert>
    )
  }

  if (!tableData.data) {
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        The spec returned no data.
      </Alert>
    )
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0 }}>
      <PlatinumTableWrapper data={tableData.data} hideControls />
    </Box>
  )
}

export default SpecRunPreviewDialog
